import React, { useState, useEffect, useContext } from 'react';
import { SessionContext } from '../contexts/SessionContext';
import { getInfo, openDoorApi } from '../services/api'; 
import { getTranslations } from '../utils/translations';
import '../styles/DoorControl.css';
import { ArrowRight, Lock } from 'lucide-react';

// Constantes para las claves de localStorage
const CODE_STORAGE_KEY = 'doorAccessAppRememberedCode'; // Actualizado para coincidir con CodeValidationPage
const COORDINATES_STORAGE_KEY = 'doorAccessCoordinates';

interface HomeAssistantResponse {
  status: number;
  message: string;
}

interface InfoData {
  house_id: number;
  seqno: number;
  rto: boolean;
  maxtime: number;
  message: string;
  nextSeqno: number;
  photo: string;
  homeAssistant?: HomeAssistantResponse;
}

interface OpenApiResponse extends InfoData {}

const DoorControlPage: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [infoData, setInfoData] = useState<InfoData | null>(null);
  const [openStatusImage, setOpenStatusImage] = useState<string | null>(null);
  const [openStatusMessage, setOpenStatusMessage] = useState<string | null>(null);
  const { houseId } = useContext(SessionContext);
  
  // Obtener traducciones según el idioma del navegador
  const translations = getTranslations();

  const resetOpenStatus = () => {
    setOpenStatusImage(null);
    setOpenStatusMessage(null);
    setError(null);
  };

  const fetchInfoData = async (currentSeqno?: number) => {
    setLoading(true);
    resetOpenStatus();
    setInfoData(null);

    if (!houseId) {
        setError(translations.sessionContextError);
        setLoading(false);
        return;
    }

    try {
      const language = navigator.language.split('-')[0].toUpperCase();
      const payload: { house_id: string; language: string; seqno?: number } = {
        house_id: houseId, 
        language: language,
      };
      if (currentSeqno !== undefined) {
        payload.seqno = currentSeqno;
      }
      const response = await getInfo(payload);
      setInfoData(response);
      setError(null);
    } catch (err: any) {
      setError(err.message || translations.connectionError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (houseId) {
        fetchInfoData(); 
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [houseId]);

  const handleNext = () => {
    if (infoData && infoData.nextSeqno > 0) {
      fetchInfoData(infoData.nextSeqno);
    }
  };

  const handleOpen = async () => {
    if (!infoData) {
        setError(translations.noInfoAvailable);
        return;
    }
    setLoading(true);
    resetOpenStatus();

    try {
      const language = navigator.language.split('-')[0].toUpperCase();
      
      // Retrieve code from localStorage (saved during code validation)
      const savedCode = localStorage.getItem(CODE_STORAGE_KEY);
      console.log('Código recuperado de localStorage:', savedCode);
      
      // Retrieve coordinates from localStorage (saved during geo validation)
      let latitude: number | undefined;
      let longitude: number | undefined;
      
      const savedCoordinates = localStorage.getItem(COORDINATES_STORAGE_KEY);
      console.log('Coordenadas recuperadas de localStorage:', savedCoordinates);
      
      if (savedCoordinates) {
        try {
          const coordinates = JSON.parse(savedCoordinates);
          latitude = coordinates.latitude;
          longitude = coordinates.longitude;
          console.log('Coordenadas parseadas:', { latitude, longitude });
        } catch (e) {
          console.warn('Error al parsear coordenadas guardadas:', e);
        }
      }
      
      // Create payload with all parameters
      const openPayload = {
        house_id: houseId,
        language: language,
        seqno: infoData.seqno,
        code: savedCode || undefined,  // Include code if available
        latitude: latitude,            // Include latitude if available
        longitude: longitude           // Include longitude if available
      };
      
      console.log("Enviando payload a /open:", JSON.stringify(openPayload, null, 2));
      const response: OpenApiResponse = await openDoorApi(openPayload);
      console.log("Open door response:", response);

      // CORRECTED LOGIC: 
      // 1. openKO.jpg ONLY if homeAssistant.status !== 200 (call failed)
      // 2. openOK.jpg ONLY if homeAssistant.status === 200 AND nextSeqno < 0 (last door)
      // 3. No status image for other cases (successful call but not last door)
      
      if (!response.homeAssistant || response.homeAssistant.status !== 200) {
        // Case 1: API call failed - show openKO.jpg
        setOpenStatusImage('/photos/openKO.jpg');
        setOpenStatusMessage(
          (response.homeAssistant?.message) || 
          response.message || 
          translations.doorOpenError
        );
      } else if (response.nextSeqno < 0) {
        // Case 2: API call successful AND it's the last door - show openOK.jpg
        setOpenStatusImage('/photos/openOK.jpg');
        setOpenStatusMessage(response.homeAssistant.message || response.message || translations.doorOpenSuccess);
        setInfoData(null); // Clear info data as the flow might end
      } else {
        // Case 3: API call successful but NOT the last door - don't show status image
        // Just reload the instructions with the current sequence number
        fetchInfoData(response.seqno);
        return; // Exit early as fetchInfoData will handle loading state
      }
    } catch (err: any) {
      console.error('Error opening door:', err);
      setOpenStatusImage('/photos/openKO.jpg');
      setOpenStatusMessage(err.message || translations.doorOpenError);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !infoData && !openStatusImage && !error) {
    return <div className="door-control-container"><div className="door-control-card"><p>{translations.loading}</p></div></div>;
  }

  if (openStatusImage) {
    return (
      <div className="door-control-container">
        <div className="door-control-card">
          <h1>{translations.doorStatus}</h1>
          <img src={openStatusImage} alt="Door open status" className="status-image" />
          {openStatusMessage && <p className={openStatusImage === '/photos/openOK.jpg' ? 'success-message' : 'error-message'}>{openStatusMessage}</p>}
          <button className="door-button styled-button" onClick={() => { resetOpenStatus(); fetchInfoData(infoData?.seqno); }}>{translations.backToInstructions}</button>
        </div>
      </div>
    );
  }
  
  if (error && !infoData) {
    return (
        <div className="door-control-container">
            <div className="door-control-card">
                <p className="error-message">Error: {error}</p>
                <button className="door-button styled-button" onClick={() => fetchInfoData()}>{translations.retryInitialLoad}</button>
            </div>
        </div>
    );
  }

  if (infoData) {
    return (
      <div className="door-control-container">
        <div className="door-control-card">
          <h1>{translations.doorAccessInstructions}</h1>
          <p className="message-text">{infoData.message}</p>
          {infoData.photo && (
            <div className="photo-container">
              <img src={`/photos/${infoData.photo}`} alt="Instruction visual" className="instruction-image"/>
            </div>
          )}
          {error && <p className="error-message">Error: {error}</p>}
          <div className="buttons-container">
            {infoData.nextSeqno > 0 && (
              <button onClick={handleNext} className="door-button styled-button next-button" disabled={loading}>
                {loading && infoData.nextSeqno > 0 ? translations.loading : translations.next}
                <ArrowRight size={20} style={{ marginLeft: '8px' }} />
              </button>
            )}
            {!infoData.rto && (
              <button onClick={handleOpen} className="door-button styled-button open-button" disabled={loading}>
                <Lock size={20} style={{ marginRight: '8px' }} />
                {loading && !infoData.nextSeqno ? translations.processing : translations.open}
              </button>
            )}
          </div>
          {loading && <p className="loading-text">{translations.processing}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="door-control-container">
        <div className="door-control-card">
            <p>{translations.waitingForSession}</p>
        </div>
    </div>
  );
};

export default DoorControlPage;
